// src/components/institution/forms/ResourceForm.jsx
import React, { useState, useEffect } from 'react';
import { 
  X, Save, FileText, Upload, Download, Link, Users, Lock, 
  Package, BookOpen, Briefcase, Monitor, Table, Shield, 
  Video, Calendar, AlertTriangle, CheckCircle, Clock, 
  MapPin, DollarSign, Hash, Tag, Eye, ExternalLink,
  Loader2, Plus, Trash2, Image as ImageIcon, File,
  Camera, Paperclip, FolderOpen
} from 'lucide-react';
import { institutionService } from '../../../services/institutionService';

// Resource type configurations (same as before)
const RESOURCE_TYPES = {
  physical: {
    label: 'Physical Resources',
    icon: Package,
    color: 'blue',
    description: 'Institution\'s physical assets and equipment',
    categories: [
      { value: 'technology', label: 'Technology & Equipment', icon: Monitor },
      { value: 'furniture', label: 'Furniture & Fixtures', icon: Table },
      { value: 'vehicles', label: 'Vehicles & Transport', icon: Package },
      { value: 'facility', label: 'Facility Infrastructure', icon: Package },
      { value: 'books', label: 'Physical Books & Library', icon: BookOpen },
      { value: 'supplies', label: 'Office & Teaching Supplies', icon: Package },
      { value: 'safety', label: 'Safety & Security Equipment', icon: Shield },
      { value: 'sports', label: 'Sports & Recreation', icon: Package }
    ]
  },
  learning: {
    label: 'Learning & Teaching Materials',
    icon: BookOpen,
    color: 'green',
    description: 'Educational content and teaching resources',
    categories: [
      { value: 'curriculum', label: 'Curriculum Materials', icon: BookOpen },
      { value: 'multimedia', label: 'Video & Audio Content', icon: Video },
      { value: 'documents', label: 'Documents & PDFs', icon: FileText },
      { value: 'interactive', label: 'Interactive Content', icon: Package },
      { value: 'assessments', label: 'Assessments & Quizzes', icon: FileText },
      { value: 'presentations', label: 'Presentations & Slides', icon: FileText },
      { value: 'external', label: 'External Links & Resources', icon: Link },
      { value: 'templates', label: 'Teaching Templates', icon: FileText }
    ]
  },
  management: {
    label: 'Management Resources',
    icon: Briefcase,
    color: 'purple',
    description: 'Administrative and governance materials',
    categories: [
      { value: 'policies', label: 'Policies & Procedures', icon: FileText },
      { value: 'plans', label: 'Strategic & Annual Plans', icon: Calendar },
      { value: 'reports', label: 'Reports & Analytics', icon: FileText },
      { value: 'legal', label: 'Legal & Compliance', icon: Shield },
      { value: 'hr', label: 'Human Resources', icon: Users },
      { value: 'finance', label: 'Financial Documents', icon: Package },
      { value: 'governance', label: 'Governance & Board', icon: Briefcase },
      { value: 'quality', label: 'Quality Assurance', icon: CheckCircle }
    ]
  }
};

const STATUS_OPTIONS = {
  available: { label: 'Available', color: 'text-green-600 bg-green-100', icon: CheckCircle },
  inUse: { label: 'In Use', color: 'text-blue-600 bg-blue-100', icon: Clock },
  maintenance: { label: 'Maintenance', color: 'text-yellow-600 bg-yellow-100', icon: AlertTriangle },
  damaged: { label: 'Damaged', color: 'text-red-600 bg-red-100', icon: AlertTriangle },
  retired: { label: 'Retired', color: 'text-gray-600 bg-gray-100', icon: Package }
};

const ACCESS_LEVELS = [
  { value: 'public', label: 'Public (Everyone)' },
  { value: 'students', label: 'Students Only' },
  { value: 'staff', label: 'Staff Only' },
  { value: 'faculty', label: 'Faculty Only' },
  { value: 'admin', label: 'Admin Only' }
];

export const ResourceForm = ({ resource, onSave, onClose, bookId }) => {
  const [formData, setFormData] = useState({
    // Basic fields
    title: '',
    description: '',
    resourceType: 'learning',
    category: '',
    tags: [],
    accessLevel: 'public',
    
    // File/Link fields
    file: null,
    fileUrl: '',
    downloadUrl: '',
    externalUrl: '',
    fileSize: 0,
    
    // Image and document uploads
    images: [], // Array of uploaded images
    documents: [], // Array of supporting documents
    
    // Physical resource specific fields
    assetId: '',
    location: '',
    status: 'available',
    purchaseDate: '',
    warranty: '',
    value: 0,
    condition: 'good',
    
    // Learning resource specific fields
    subject: '',
    gradeLevel: '',
    difficulty: 'intermediate',
    duration: '',
    
    // Management resource specific fields
    department: '',
    priority: 'medium',
    reviewDate: '',
    approvedBy: '',
    
    // Metadata
    version: '1.0',
    uploadedBy: '',
    isActive: true
  });

  const [uploading, setUploading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [errors, setErrors] = useState({});
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (resource) {
      setFormData({
        ...formData,
        ...resource,
        tags: resource.tags || [],
        images: resource.images || [],
        documents: resource.documents || []
      });
    }
  }, [resource]);

  // Update category when resource type changes
  useEffect(() => {
    if (formData.resourceType && RESOURCE_TYPES[formData.resourceType]) {
      const firstCategory = RESOURCE_TYPES[formData.resourceType].categories[0];
      if (!formData.category || !RESOURCE_TYPES[formData.resourceType].categories.find(c => c.value === formData.category)) {
        setFormData(prev => ({ ...prev, category: firstCategory.value }));
      }
    }
  }, [formData.resourceType]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Main file upload (for digital resources)
  const handleMainFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadedUrl = await institutionService.uploadDocument(file, bookId, 'resources');
      
      setFormData(prev => ({
        ...prev,
        fileUrl: uploadedUrl,
        downloadUrl: uploadedUrl,
        fileSize: file.size,
        file: {
          name: file.name,
          size: file.size,
          type: file.type
        }
      }));
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  // Image upload handler
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const uploadedUrl = await institutionService.uploadDocument(file, bookId, 'resource-images');
        return {
          id: Date.now() + Math.random(),
          name: file.name,
          url: uploadedUrl,
          size: file.size,
          type: file.type,
          uploadedAt: new Date()
        };
      });

      const uploadedImages = await Promise.all(uploadPromises);
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedImages]
      }));
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Failed to upload images');
    } finally {
      setUploadingImages(false);
    }
  };

  // Document upload handler
  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingDocuments(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const uploadedUrl = await institutionService.uploadDocument(file, bookId, 'resource-documents');
        return {
          id: Date.now() + Math.random(),
          name: file.name,
          url: uploadedUrl,
          size: file.size,
          type: file.type,
          uploadedAt: new Date()
        };
      });

      const uploadedDocuments = await Promise.all(uploadPromises);
      
      setFormData(prev => ({
        ...prev,
        documents: [...prev.documents, ...uploadedDocuments]
      }));
    } catch (error) {
      console.error('Error uploading documents:', error);
      alert('Failed to upload documents');
    } finally {
      setUploadingDocuments(false);
    }
  };

  // Remove image
  const handleRemoveImage = (imageId) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter(img => img.id !== imageId)
    }));
  };

  // Remove document
  const handleRemoveDocument = (docId) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter(doc => doc.id !== docId)
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Resource title is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.resourceType) {
      newErrors.resourceType = 'Resource type is required';
    }
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    // Physical resource validation
    if (formData.resourceType === 'physical') {
      if (!formData.assetId.trim()) {
        newErrors.assetId = 'Asset ID is required for physical resources';
      }
      if (!formData.location.trim()) {
        newErrors.location = 'Location is required for physical resources';
      }
    }

    // Digital resource validation
    if ((formData.resourceType === 'learning' || formData.resourceType === 'management') && 
        !formData.fileUrl && !formData.externalUrl) {
      newErrors.file = 'Please upload a file or provide an external link';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await onSave({
        ...formData,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error saving resource:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    if (type.includes('image')) return ImageIcon;
    if (type.includes('pdf')) return FileText;
    if (type.includes('video')) return Video;
    if (type.includes('audio')) return Video;
    return File;
  };

  const currentTypeConfig = RESOURCE_TYPES[formData.resourceType];
  const availableCategories = currentTypeConfig?.categories || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${
              currentTypeConfig?.color === 'blue' ? 'bg-blue-100 text-blue-600' :
              currentTypeConfig?.color === 'green' ? 'bg-green-100 text-green-600' :
              'bg-purple-100 text-purple-600'
            }`}>
              {currentTypeConfig?.icon && <currentTypeConfig.icon className="h-5 w-5" />}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {resource ? 'Edit Resource' : 'Add New Resource'}
              </h2>
              <p className="text-sm text-gray-600">{currentTypeConfig?.description}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Resource Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Resource Type *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {Object.entries(RESOURCE_TYPES).map(([type, config]) => {
                  const IconComponent = config.icon;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handleInputChange('resourceType', type)}
                      className={`p-4 border rounded-lg text-left transition-all ${
                        formData.resourceType === type
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <IconComponent className="h-5 w-5" />
                        <span className="font-medium">{config.label}</span>
                      </div>
                      <p className="text-xs text-gray-600">{config.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.title ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter resource title"
                />
                {errors.title && (
                  <p className="text-red-600 text-xs mt-1">{errors.title}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Category</option>
                  {availableCategories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
                {errors.category && (
                  <p className="text-red-600 text-xs mt-1">{errors.category}</p>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.description ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Describe the resource and its purpose"
              />
              {errors.description && (
                <p className="text-red-600 text-xs mt-1">{errors.description}</p>
              )}
            </div>

            {/* Images Upload Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Camera className="h-5 w-5 text-blue-600" />
                  Resource Images
                </h3>
                
                <div>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                    disabled={uploadingImages}
                  />
                  <label
                    htmlFor="image-upload"
                    className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm border border-blue-300"
                  >
                    {uploadingImages ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                    {uploadingImages ? 'Uploading...' : 'Add Images'}
                  </label>
                </div>
              </div>

              {/* Images Grid */}
              {formData.images.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {formData.images.map((image) => (
                    <div key={image.id} className="relative group">
                      <img
                        src={image.url}
                        alt={image.name}
                        className="w-full h-32 object-cover rounded-lg border border-gray-200"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 rounded-lg transition-all flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 flex gap-2">
                          <button
                            type="button"
                            onClick={() => window.open(image.url, '_blank')}
                            className="p-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(image.id)}
                            className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-1 truncate">{image.name}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Supporting Documents Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Paperclip className="h-5 w-5 text-green-600" />
                  Supporting Documents
                </h3>
                
                <div>
                  <input
                    type="file"
                    multiple
                    onChange={handleDocumentUpload}
                    className="hidden"
                    id="document-upload"
                    disabled={uploadingDocuments}
                  />
                  <label
                    htmlFor="document-upload"
                    className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-sm border border-green-300"
                  >
                    {uploadingDocuments ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Paperclip className="h-4 w-4" />
                    )}
                    {uploadingDocuments ? 'Uploading...' : 'Add Documents'}
                  </label>
                </div>
              </div>

              {/* Documents List */}
              {formData.documents.length > 0 && (
                <div className="space-y-2">
                  {formData.documents.map((document) => {
                    const FileIconComponent = getFileIcon(document.type);
                    return (
                      <div key={document.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <FileIconComponent className="h-5 w-5 text-gray-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {document.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(document.size)} • {new Date(document.uploadedAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => window.open(document.url, '_blank')}
                            className="p-1 text-gray-400 hover:text-blue-600"
                            title="View Document"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveDocument(document.id)}
                            className="p-1 text-gray-400 hover:text-red-600"
                            title="Remove Document"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <p className="text-sm text-gray-600">
                Upload supporting documents such as purchase receipts, warranties, logbooks, manuals, etc.
              </p>
            </div>

            {/* Physical Resource Specific Fields */}
            {formData.resourceType === 'physical' && (
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="font-medium text-blue-900 flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Physical Asset Details
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Asset ID *
                    </label>
                    <input
                      type="text"
                      value={formData.assetId}
                      onChange={(e) => handleInputChange('assetId', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        errors.assetId ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="e.g., AST001"
                    />
                    {errors.assetId && (
                      <p className="text-red-600 text-xs mt-1">{errors.assetId}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Location *
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        errors.location ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="e.g., Room 101, Lab A"
                    />
                    {errors.location && (
                      <p className="text-red-600 text-xs mt-1">{errors.location}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.entries(STATUS_OPTIONS).map(([key, status]) => (
                        <option key={key} value={key}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Purchase Date
                    </label>
                    <input
                      type="date"
                      value={formData.purchaseDate}
                      onChange={(e) => handleInputChange('purchaseDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Value ($)
                    </label>
                    <input
                      type="number"
                      value={formData.value}
                      onChange={(e) => handleInputChange('value', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Condition
                    </label>
                    <select
                      value={formData.condition}
                      onChange={(e) => handleInputChange('condition', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="excellent">Excellent</option>
                      <option value="good">Good</option>
                      <option value="fair">Fair</option>
                      <option value="poor">Poor</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Warranty Information
                  </label>
                  <textarea
                    value={formData.warranty}
                    onChange={(e) => handleInputChange('warranty', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Warranty details, expiration date, etc."
                  />
                </div>
              </div>
            )}

            {/* Digital Resource Fields (Learning & Management) */}
            {(formData.resourceType === 'learning' || formData.resourceType === 'management') && (
              <div className="space-y-4">
                {/* Main File Upload or External Link */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <FolderOpen className="h-4 w-4" />
                    Primary Resource File or Link
                  </h3>
                  
                  <div className="space-y-3">
                    {/* File Upload */}
                    <div>
                      <input
                        type="file"
                        onChange={handleMainFileUpload}
                        className="hidden"
                        id="main-file-upload"
                        disabled={uploading}
                      />
                      <label
                        htmlFor="main-file-upload"
                        className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm border border-gray-300"
                      >
                        {uploading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Upload className="h-4 w-4" />
                        )}
                        {uploading ? 'Uploading...' : 'Upload Primary File'}
                      </label>
                      
                      {formData.fileUrl && (
                        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center gap-2 text-sm text-green-800">
                            <CheckCircle className="h-4 w-4" />
                            File uploaded successfully
                            {formData.fileSize && (
                              <span className="text-gray-600">({formatFileSize(formData.fileSize)})</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* External Link */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Or External Link
                      </label>
                      <input
                        type="url"
                        value={formData.externalUrl}
                        onChange={(e) => handleInputChange('externalUrl', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="https://example.com/resource"
                      />
                    </div>
                  </div>
                  {errors.file && (
                    <p className="text-red-600 text-xs mt-1">{errors.file}</p>
                  )}
                </div>

                {/* Learning Resource Specific Fields */}
                {formData.resourceType === 'learning' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Subject
                      </label>
                      <input
                        type="text"
                        value={formData.subject}
                        onChange={(e) => handleInputChange('subject', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        placeholder="e.g., Mathematics, Science"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Grade Level
                      </label>
                      <input
                        type="text"
                        value={formData.gradeLevel}
                        onChange={(e) => handleInputChange('gradeLevel', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        placeholder="e.g., Grade 10, University"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Difficulty
                      </label>
                      <select
                        value={formData.difficulty}
                        onChange={(e) => handleInputChange('difficulty', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      >
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Management Resource Specific Fields */}
                {formData.resourceType === 'management' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Department
                      </label>
                      <input
                        type="text"
                        value={formData.department}
                        onChange={(e) => handleInputChange('department', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="e.g., HR, Finance, Academic"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Priority
                      </label>
                      <select
                        value={formData.priority}
                        onChange={(e) => handleInputChange('priority', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Review Date
                      </label>
                      <input
                        type="date"
                        value={formData.reviewDate}
                        onChange={(e) => handleInputChange('reviewDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-blue-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Add tag..."
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 flex items-center gap-1"
                >
                  <Plus className="h-4 w-4" />
                  Add
                </button>
              </div>
            </div>

            {/* Access Level and Additional Metadata */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Access Level
                </label>
                <select
                  value={formData.accessLevel}
                  onChange={(e) => handleInputChange('accessLevel', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {ACCESS_LEVELS.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Version
                </label>
                <input
                  type="text"
                  value={formData.version}
                  onChange={(e) => handleInputChange('version', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 1.0, 2.1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Uploaded By
                </label>
                <input
                  type="text"
                  value={formData.uploadedBy}
                  onChange={(e) => handleInputChange('uploadedBy', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Name of uploader"
                />
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploading || uploadingImages || uploadingDocuments}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {(uploading || uploadingImages || uploadingDocuments) ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {resource ? 'Update Resource' : 'Add Resource'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};